import { useEffect, useMemo, useState } from "react";

import { Box, Tooltip } from "@mantine/core";
import { Calendar } from "@mantine/dates";
import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/sr";

import i18n from "config/localization/i18n";
import {
  DashboardCalendarEventResponse,
  DashboardCalendarEventType,
  DashboardCalendarParams,
  DashboardCalendarResponse,
} from "types";
import { DateUtils } from "utils";

import classes from "../agent/CalendarSection.module.css";

const WEEKEND_TEXT_COLOR = "var(--mantine-color-red-6)";

type CalendarEventVariant = "holiday" | "replacement" | "absence";

interface DayDetails {
  background?: string;
  priority: number;
  tooltipLabels: string[];
}

interface DashboardCalendarSectionProps {
  className?: string;
  compact?: boolean;
  loadCalendar: (params: DashboardCalendarParams) => Promise<DashboardCalendarResponse>;
  selectedAgentIds?: number[] | null;
}

const parseLocalDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const getDateRange = (dateFrom: string, dateTo: string) => {
  const startDate = parseLocalDate(dateFrom);
  const endDate = parseLocalDate(dateTo);
  const result: string[] = [];

  for (
    const currentDate = new Date(startDate);
    currentDate <= endDate;
    currentDate.setDate(currentDate.getDate() + 1)
  ) {
    result.push(DateUtils.formatDateLocal(currentDate));
  }

  return result;
};

const isWeekendDay = (date: Date) => date.getDay() === 0 || date.getDay() === 6;

const formatWeekdayLabel = (date: Date, locale: string) => {
  const label = dayjs(date).locale(locale).format("dd");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const EVENT_VARIANTS_BY_TYPE: Record<DashboardCalendarEventType, CalendarEventVariant> = {
  HOLIDAY: "holiday",
  ABSENCE: "absence",
  COVERAGE: "replacement",
};

const EVENT_PRIORITIES: Record<CalendarEventVariant, number> = {
  holiday: 3,
  replacement: 2,
  absence: 1,
};

const EVENT_BACKGROUNDS: Record<CalendarEventVariant, string> = {
  holiday: "var(--mantine-color-violet-6)",
  replacement: "var(--mantine-color-orange-6)",
  absence: "var(--mantine-color-primary-6)",
};

const formatTooltipLabel = (event: DashboardCalendarEventResponse) =>
  event.agentName ? `${event.title} - ${event.agentName}` : event.title;

const DashboardCalendarSection = ({
  className,
  compact = false,
  loadCalendar,
  selectedAgentIds,
}: DashboardCalendarSectionProps) => {
  const [displayedDate, setDisplayedDate] = useState(new Date());
  const [events, setEvents] = useState<DashboardCalendarEventResponse[]>([]);
  const calendarLocale = i18n.language.toLowerCase().split("-")[0];

  const visibleRange = useMemo(() => {
    const start = dayjs(displayedDate).startOf("month").toDate();
    const end = dayjs(displayedDate).endOf("month").toDate();

    return {
      from: DateUtils.formatDateLocal(start),
      to: DateUtils.formatDateLocal(end),
    };
  }, [displayedDate]);

  const requestParams = useMemo(
    () => ({
      ...visibleRange,
      ...(selectedAgentIds?.length ? { agentIds: selectedAgentIds } : {}),
    }),
    [selectedAgentIds, visibleRange],
  );

  useEffect(() => {
    let mounted = true;

    const loadEvents = async () => {
      try {
        const data = await loadCalendar(requestParams);

        if (!mounted) {
          return;
        }

        setEvents(Array.isArray(data?.events) ? data.events : []);
      } catch {
        if (!mounted) {
          return;
        }

        setEvents([]);
      }
    };

    loadEvents();

    return () => {
      mounted = false;
    };
  }, [loadCalendar, requestParams]);

  const dayDetailsByDate = useMemo(() => {
    const map = new Map<string, DayDetails>();

    const ensureDayDetails = (dateString: string) => {
      const existingDay = map.get(dateString);

      if (existingDay) {
        return existingDay;
      }

      const nextDay: DayDetails = {
        background: undefined,
        priority: 0,
        tooltipLabels: [],
      };

      map.set(dateString, nextDay);
      return nextDay;
    };

    events.forEach((event) => {
      const variant = EVENT_VARIANTS_BY_TYPE[event.type];
      const priority = EVENT_PRIORITIES[variant];
      const background = EVENT_BACKGROUNDS[variant];
      const label = formatTooltipLabel(event);

      getDateRange(event.dateFrom, event.dateTo).forEach((dateString) => {
        const details = ensureDayDetails(dateString);

        if (priority >= details.priority) {
          details.priority = priority;
          details.background = background;
        }

        if (!details.tooltipLabels.includes(label)) {
          details.tooltipLabels.push(label);
        }
      });
    });

    return map;
  }, [events]);

  return (
    <div
      className={[
        classes.root,
        compact ? classes.compactRoot : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Box className={classes.calendarShell}>
        <Calendar
          static
          date={displayedDate}
          onDateChange={setDisplayedDate}
          highlightToday={false}
          locale={calendarLocale}
          size={compact ? "xs" : "lg"}
          withCellSpacing={!compact}
          weekendDays={[0, 6]}
          maxLevel="month"
          minLevel="month"
          styles={
            compact
              ? {
                  calendarHeader: {
                    marginBottom: 2,
                    minHeight: 24,
                  },
                  calendarHeaderControl: {
                    width: 22,
                    minWidth: 22,
                    height: 22,
                    padding: 0,
                  },
                  calendarHeaderControlIcon: {
                    width: 14,
                    height: 14,
                  },
                  calendarHeaderLevel: {
                    fontSize: "13px",
                    fontWeight: 500,
                    lineHeight: 1.1,
                  },
                  weekdaysRow: {
                    marginBottom: 0,
                  },
                  weekday: {
                    fontSize: "11px",
                    fontWeight: 500,
                    paddingBottom: 1,
                  },
                }
              : undefined
          }
          hideOutsideDates={compact}
          weekdayFormat={(date) => {
            const label = formatWeekdayLabel(date, calendarLocale);

            if (!isWeekendDay(date)) {
              return label;
            }

            return <span className={classes.weekendWeekday}>{label}</span>;
          }}
          getDayProps={(date) => {
            const dateString = DateUtils.formatDateLocal(date);
            const details = dayDetailsByDate.get(dateString);
            const isMarked = Boolean(details?.background);
            const todayString = DateUtils.formatDateLocal(new Date());
            const isToday = dateString === todayString;
            const isWeekend = isWeekendDay(date);

            return {
              style: {
                background: details?.background,
                border:
                  isToday && !isMarked
                    ? "1.5px solid var(--mantine-color-gray-7)"
                    : "1.5px solid transparent",
                borderRadius: 8,
                color: isMarked
                  ? "var(--mantine-color-white)"
                  : isWeekend
                    ? WEEKEND_TEXT_COLOR
                    : undefined,
                fontWeight: isToday || isMarked ? 600 : 500,
              },
            };
          }}
          renderDay={(date) => {
            const dateString = DateUtils.formatDateLocal(date);
            const details = dayDetailsByDate.get(dateString);
            const dayContent = <div className={classes.dayCell}>{date.getDate()}</div>;

            if (!details?.tooltipLabels.length) {
              return dayContent;
            }

            return (
              <Tooltip
                label={(
                  <div className={classes.tooltipContent}>
                    {details.tooltipLabels.map((label) => (
                      <div key={label} className={classes.tooltipLine}>
                        {label}
                      </div>
                    ))}
                  </div>
                )}
                multiline
                position="top"
                withArrow
                withinPortal={false}
              >
                <div className={classes.dayTooltipTarget}>{dayContent}</div>
              </Tooltip>
            );
          }}
        />
      </Box>
    </div>
  );
};

export default DashboardCalendarSection;
